import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { Col, Modal, ModalBody, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import {
  Desktop,
  Expand,
  Mic,
  MicOff,
  Phone,
  PhoneMissed,
  Recording,
  StopRecording,
  StopScreen,
  Video,
  VideoAlt,
  VideoOff
} from '../../assets/icons/jsxIcons';
import './videochat.css';
import { user } from '../../utils/api/user';
import { createRecording } from '../../utils/api/message';
import { useRequest } from '../../utils/hooks';
import { Pressable, Text } from '../theme';
import { useRootMemoSelector } from 'use-redux-states';
import { Avatar, Card, Row } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useVideoCallContext } from '@sawport/peers-caller';
import { setConversationId, setOtherUserId } from '../../store/call';
import { useDispatch } from 'react-redux';
import FeatureGate from './FeatureGate';
import { useFeature } from '../../zustand/features';

// Constants
const CONTAINER_HEIGHT = 250;
const FULLSCREEN_CONTAINER_ID = 'video-call-container';
const RECORDING_BORDER_COLOR = '#db3814';
const RECORDING_BORDER_WIDTH = '4px solid';
const PARTICIPANT_NAME_BG = 'rgba(0, 0, 0, 0.4)';
const CONTROL_BTN_BG = 'rgba(0, 0, 0, 0.4)';
const END_CALL_BTN_COLOR = '#dc2626';

// Utility functions
const capitalizeWords = (str) => {
  if (!str) return '';
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const getParticipantDisplayName = (participant) => {
  if (!participant) return 'Unknown User';
  const src = participant.user ?? participant;

  const formatName = (obj) => {
    const first = obj?.first_name || obj?.firstName || '';
    const last = obj?.last_name || obj?.lastName || '';
    const full = [first, last].filter(Boolean).map(capitalizeWords).join(' ').trim();
    return full || (obj?.name ? capitalizeWords(obj.name) : '');
  };

  const pickName = (obj, email, fallback) => formatName(obj) || email || fallback;

  const type =
    src.user_type ||
    (src.agent && 'agent') ||
    (src.customer && 'customer') ||
    (src.guest && 'guest');

  if (type === 'agent' && (src.agent || participant.agent)) {
    const a = src.agent || participant.agent;
    return pickName(a, a?.email, 'Agent');
  }

  if (type === 'customer' && (src.customer || participant.customer)) {
    const c = src.customer || participant.customer;
    return pickName(c, c?.email, 'Customer');
  }

  if (type === 'guest' && (src.guest || participant.guest)) {
    const g = src.guest || participant.guest;
    if (!g?.first_name && !g?.firstName && g?.name) {
      const [f, ...rest] = g.name.split(' ');
      const full = `${capitalizeWords(f || '')} ${capitalizeWords(rest.join(' ') || '')}`.trim();
      return full || 'Guest';
    }
    return pickName(g, undefined, 'Guest');
  }

  if (participant.displayName) return participant.displayName;

  const srcName = pickName(src, src?.email, '');
  if (srcName) return srcName;

  const pName = pickName(participant, participant?.email, '');
  if (pName) return pName;

  return String(src.userId || src.id || participant.userId || participant.id || 'Unknown User');
};

function VideoChat({ style = {}, conversation_id: conversationId, floating = false }) {
  // Redux selectors
  const userId = useRootMemoSelector('auth.user.user.id');
  const userObj = useRootMemoSelector('auth.user');

  // Use video call context from provider
  const {
    initialize,
    startCall,
    joinCall,
    endCall,
    toggleAudio,
    toggleVideo,
    startScreenShare,
    stopScreenShare,
    startRecording,
    stopRecording,
    participants: mapParticipants,
    isConnected: isCallConnected,
    isRecording,
    callState,
    peersCaller,
    localParticipant
  } = useVideoCallContext();

  const dispatch = useDispatch();
  const hasCallRecording = useFeature('call_recording');
  const hasScreenSharing = useFeature('screen_sharing');

  // map participants to array
  const participants = useMemo(() => Array.from(mapParticipants.values()), [mapParticipants]);

  const initCall = useCallback(async () => {
    try {
      if (peersCaller && !isCallConnected) {
        if (!callState?.callStatusInfo?.hasActiveCall && isCallConnected) {
          await joinCall();
        } else {
          await startCall();
        }
      } else if (!peersCaller && !isCallConnected) {
        await initialize();
      }
    } catch (error) {
      console.error(error);
    }
  }, [callState?.callStatusInfo?.hasActiveCall, isCallConnected]);

  useEffect(() => {
    initCall();
  }, [initCall]);

  useEffect(() => {
    if (peersCaller && !isCallConnected) {
      peersCaller.checkCallStatus();
    }
  }, [peersCaller, isCallConnected]);

  // Refs for legacy layout
  const mainRemoteRef = useRef(null);
  const localRef = useRef(null);

  // Cleanup effect to ensure all media streams are released when component unmounts
  useEffect(() => {
    return () => {
      // Clean up local video element
      if (localRef.current) {
        localRef.current.srcObject = null;
      }
      // Clean up remote video element
      if (mainRemoteRef.current) {
        mainRemoteRef.current.srcObject = null;
      }
    };
  }, []);

  const startCallRecording = useCallback(async () => {
    if (!hasCallRecording) {
      toast.error('Call recording is not licensed for your organization.');
      return;
    }

    // Prevent starting recording if already in progress
    if (isRecording) {
      toast.info('Recording is already in progress');
      return;
    }

    const { data: recordingResponse } = await createRecording({
      conversation: conversationId
    });

    // Step 2: Start recording with metadata from API response
    const recordingData = {
      id: recordingResponse.id,
      filename: recordingResponse.filename,
      roomId: conversationId,
      startTime: Date.now()
    };

    await startRecording(recordingData);
  }, [isRecording, startRecording, conversationId]);

  const stopCallRecording = useCallback(async () => {
    try {
      await stopRecording();
    } catch (error) {
      toast.error('Failed to stop recording. Please try again.');
    }
  }, [stopRecording]);

  const handleStartShare = useCallback(async () => {
    if (!hasScreenSharing) {
      toast.error('Screen sharing is not licensed for your organization.');
      return;
    }
    try {
      await startScreenShare();
    } catch (e) {
      // no-op; keep state as-is
    }
  }, [startScreenShare, hasScreenSharing]);

  const handleStopShare = useCallback(() => {
    stopScreenShare();
  }, [stopScreenShare]);

  const useGrid = participants.length > 2;

  const remoteParticipant = useMemo(() => {
    return participants.find((p) => {
      const participantId = p.userId || p.user?.id || p.id;
      return participantId !== String(userId);
    });
  }, [participants, userId]);

  const onEndCall = useCallback(async () => {
    try {
      await endCall();
      dispatch(setOtherUserId(undefined));
      dispatch(setConversationId(undefined));
    } catch (error) {
      console.error('Error ending call:', error);
    }
  }, [endCall]);

  const onToggleAudio = useCallback(
    () => toggleAudio(!(localParticipant?.audioOn ?? true)),
    [localParticipant, toggleAudio]
  );

  const onToggleVideo = useCallback(
    () => toggleVideo(!(localParticipant?.videoOn ?? true)),
    [localParticipant, toggleVideo]
  );

  useEffect(() => {
    if (localRef.current) {
      localRef.current.srcObject = localParticipant?.stream || null;
    }

    // Cleanup when component unmounts or stream changes
    return () => {
      if (localRef.current) {
        localRef.current.srcObject = null;
      }
    };
  }, [localParticipant?.stream, localParticipant?.videoOn, localParticipant?.screenShared]);

  useEffect(() => {
    if (mainRemoteRef.current) {
      mainRemoteRef.current.srcObject = remoteParticipant?.stream || null;
    }

    // Cleanup when component unmounts or stream changes
    return () => {
      if (mainRemoteRef.current) {
        mainRemoteRef.current.srcObject = null;
      }
    };
  }, [remoteParticipant?.stream, remoteParticipant?.videoOn, remoteParticipant?.screenShared]);

  return (
    <div
      id={FULLSCREEN_CONTAINER_ID}
      style={{
        height: `${CONTAINER_HEIGHT}px`,
        overflow: 'hidden',
        ...(isRecording ? { border: `${RECORDING_BORDER_WIDTH} ${RECORDING_BORDER_COLOR}` } : {}),
        ...style
      }}
      className={`chat-interaction shadow-lg`}>
      {useGrid ? (
        <VideoGrid participants={participants} />
      ) : (
        <>
          <div className='chat-incoming relative'>
            {!remoteParticipant?.videoOn && !remoteParticipant?.screenShared && (
              <VideoOffCard className='border-0' />
            )}
            <video
              ref={mainRemoteRef}
              style={{ objectFit: 'cover' }}
              className='w-full h-full'
              id='remote-video'
              autoPlay
              playsInline
            />
            {/* Remote participant name */}
            {remoteParticipant && (
              <span
                className='absolute bottom-2 left-2 text-white text-xs px-2 py-1 rounded'
                style={{ backgroundColor: PARTICIPANT_NAME_BG }}>
                {getParticipantDisplayName(remoteParticipant)}
              </span>
            )}
          </div>
          <div className='chat-out rounded-xl relative'>
            {!localParticipant?.videoOn && !localParticipant?.screenShared && (
              <VideoOffCard className='' />
            )}
            <video
              ref={localRef}
              style={{ objectFit: 'cover' }}
              id='local-video'
              className='w-full h-full'
              autoPlay
              muted
              playsInline
            />
            {/* Local participant name */}
            {localParticipant && (
              <span className='absolute bottom-2 left-2 text-white text-xs bg-black/40 px-2 py-1 rounded'>
                {(() => {
                  const user = userObj?.user;
                  if (user?.first_name && user?.last_name) {
                    return `${user.first_name} ${user.last_name}`;
                  }
                  return user?.name || userObj?.name || localParticipant?.displayName || 'You';
                })()}
              </span>
            )}
          </div>
        </>
      )}

      {isRecording && (
        <Recording
          fill='#fff'
          width={42}
          role='img'
          aria-label='Recording in progress'
          style={{ position: 'absolute', top: 10, left: floating ? 16 : 10 }}
        />
      )}
      <Controls
        isRecording={isRecording}
        onStartRecording={startCallRecording}
        onStopRecording={stopCallRecording}
        onToggleAudio={onToggleAudio}
        onToggleVideo={onToggleVideo}
        onStartScreenShare={handleStartShare}
        onStopScreenShare={handleStopShare}
        onEndCall={onEndCall}
        localParticipant={localParticipant}
        screenShared={localParticipant?.screenSharing}
      />
    </div>
  );
}

// Legacy subcomponents removed in favor of peers-caller managed refs above

const VideoOffCard = ({ className }) => (
  <Card className={`flex w-full h-full items-center justify-center bg-gray-900 ${className}`}>
    <Avatar icon={<UserOutlined />} />
  </Card>
);

const Controls = ({
  isRecording,
  onStartRecording,
  onStopRecording,
  onToggleAudio,
  onToggleVideo,
  onStartScreenShare,
  onStopScreenShare,
  onEndCall,
  localParticipant,
  screenShared
}) => {
  const muted = !localParticipant?.audioOn;
  const videoOff = !(localParticipant?.videoOn ?? true);
  const recordLoading = false;

  const goFullScreen = useCallback(() => {
    const el = document.getElementById('video-call-container');
    if (!el) return;

    if (document.fullscreenElement) {
      // Currently in fullscreen, exit it
      document.exitFullscreen();
    } else {
      // Not in fullscreen, enter it
      el.requestFullscreen && el.requestFullscreen();
    }
  }, []);

  return (
    <Row
      id='video-call-actions'
      justify='end'
      align='middle'
      className='p-2 pr-4'
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        display: 'none'
      }}>
      <FeatureGate feature='call_recording'>
        <ActionBtn
          aria-label={isRecording ? 'Stop recording' : 'Start recording'}
          disabled={recordLoading}
          onClick={isRecording ? onStopRecording : onStartRecording}>
          <StopRecording fill={isRecording ? '#f00' : '#fff'} width={16} height={16} />
        </ActionBtn>
      </FeatureGate>
      <ActionBtn aria-label='Toggle full screen' onClick={goFullScreen}>
        <Expand width={16} height={16} />
      </ActionBtn>
      <ActionBtn aria-label={muted ? 'Unmute microphone' : 'Mute microphone'} onClick={onToggleAudio}>
        {muted ? <MicOff fill='#fff' width={16} height={16} /> : <Mic width={16} height={16} />}
      </ActionBtn>
      <ActionBtn aria-label={videoOff ? 'Turn camera on' : 'Turn camera off'} onClick={onToggleVideo}>
        {videoOff ? (
          <VideoOff fill='#fff' width={16} height={16} />
        ) : (
          <VideoAlt width={16} height={16} />
        )}
      </ActionBtn>
      <FeatureGate feature='screen_sharing'>
        <ActionBtn
          aria-label={screenShared ? 'Stop sharing screen' : 'Share screen'}
          onClick={screenShared ? onStopScreenShare : onStartScreenShare}>
          {screenShared ? <StopScreen width={16} height={16} /> : <Desktop width={16} height={16} />}
        </ActionBtn>
      </FeatureGate>

      <ActionBtn aria-label='End call' style={{ backgroundColor: END_CALL_BTN_COLOR }} onClick={onEndCall}>
        <PhoneMissed width={16} height={16} />
      </ActionBtn>
    </Row>
  );
};

const ActionBtn = ({ children, style = {}, ...props }) => (
  <button
    type='button'
    title={props['aria-label']}
    style={{ backgroundColor: CONTROL_BTN_BG, ...style }}
    className='video-call-action mx-2 text-white'
    {...props}>
    {children}
  </button>
);

export const IncomingVideoCallModal = memo(({ isIncoming }) => {
  const {
    state: { isLoading, data: { data } = {} }
  } = useRequest(
    {
      asyncRequest: user,
      params: [undefined],
      loadOnMount: false
    },
    []
  );

  return (
    <Modal
      backdrop={false}
      toggle={() => { }}
      contentClassName='border-0'
      className='rounded'
      style={styles.incomingModal}
      isOpen={isIncoming}>
      <ModalBody style={styles.incomingModalBody} className='flex flex-column  items-center'>
        <Row className='mt-5' justify='center' align='middle'>
          <Col>
            <div style={styles.line} />
          </Col>
          <Col>
            <Text className='text-white'>Incoming Video Call</Text>
          </Col>
          <Col>
            <div style={styles.line} />
          </Col>
        </Row>
        <div style={styles.imgCont} className='justify-center mb-5'>
          <img src={data?.profile?.picture} alt='profile' style={styles.img} />
        </div>
        {isLoading ? (
          <Spinner className='flex items-center justify-center' />
        ) : (
          <>
            <h1 className='text-white text-center mt-5'>
              {data?.profile?.name} {data?.profile?.last_name}
            </h1>
            <Text className='text-white text-center'>{data?.team}</Text>
          </>
        )}
        <Row className='mt-5' gutter={16}>
          <Col md={6}>
            <Pressable
              className='flex rounded-circle bg-danger items-center justify-center'
              style={{ ...styles.callBtn, ...styles.rejectIcon }}
              onClick={() => { }}>
              <Phone width={40} fill='#fff' stroke={null} />
            </Pressable>
          </Col>
          <Col md={6}>
            <Pressable
              style={styles.callBtn}
              className='flex rounded-circle bg-success items-center justify-center'
              onClick={() => { }}>
              <Video width={40} />
            </Pressable>
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  );
});

const styles = {
  callBtn: {
    width: 90,
    height: 90
  },
  rejectIcon: {
    transform: 'rotateZ(130deg)'
  },
  img: {
    width: 150,
    height: 150
  },
  imgCont: {
    width: 150,
    height: 150,
    backgroundColor: 'gray',
    borderRadius: '50%',
    overflow: 'hidden',
    marginTop: '6rem'
  },
  line: {
    backgroundColor: '#fff',
    width: 180,
    height: 1
  },
  incomingModalBody: {
    backgroundColor: '#083147',
    width: 600,
    height: 600
  },
  incomingModal: {
    width: 600
  }
};

export default memo(VideoChat);

// Layout: Agent on left (full height), remaining participants stacked on right
const VideoGrid = memo(function VideoGrid({ participants }) {
  // Filter out participants without active streams (disconnected/left participants)

  if (participants.length === 4) {
    // 2x2 grid for 4 participants, each tile fills its cell and the grid fills the container
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: '1fr 1fr',
          width: '100%',
          height: '100%',
          gap: 0,
          padding: 0,
          margin: 0
        }}>
        {participants.map((p, idx) => {
          const tileContent = <VideoTile participant={p} />;
          return tileContent ? (
            <div
              key={p.userId || p.user?.id || p.id || idx}
              style={{
                width: '100%',
                height: '100%',
                minWidth: 0,
                minHeight: 0,
                overflow: 'hidden',
                display: 'flex'
              }}>
              {tileContent}
            </div>
          ) : null;
        })}
      </div>
    );
  }

  // 3 or fewer: agent/self on left, others stacked right
  const agentParticipant = participants[0];
  const remoteParticipants = participants.slice(1, 4);
  return (
    <div style={{ display: 'flex', width: '100%', height: '100%' }}>
      <div style={{ width: '50%', height: '100%' }}>
        {agentParticipant && <VideoTile participant={agentParticipant} isFullHeight />}
      </div>
      <div style={{ width: '50%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        {remoteParticipants.map((p) => {
          const tileContent = <VideoTile participant={p} />;
          return tileContent ? (
            <div key={p.userId || p.user?.id || p.id} style={{ flex: 1, minHeight: 0 }}>
              {tileContent}
            </div>
          ) : null;
        })}
      </div>
    </div>
  );
});

const VideoTile = memo(({ participant }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      // @ts-ignore
      ref.current.srcObject = participant?.stream || null;
    }

    // Cleanup: clear the video element when participant leaves or stream is removed
    return () => {
      if (ref.current) {
        // @ts-ignore
        ref.current.srcObject = null;
      }
    };
  }, [participant?.stream]);

  // Don't render if participant has no stream
  // (This handles when participant explicitly disconnects)
  if (!participant?.stream) {
    return null;
  }

  return (
    <div className='relative overflow-hidden bg-black' style={{ width: '100%', height: '100%' }}>
      {!participant?.videoOn && !participant?.screenShared && <VideoOffCard className='border-0' />}
      <video ref={ref} autoPlay playsInline className='w-full h-full object-cover' />
      <span
        className='absolute bottom-2 left-2 text-white text-xs px-2 py-1 rounded'
        style={{ backgroundColor: PARTICIPANT_NAME_BG }}>
        {getParticipantDisplayName(participant)}
      </span>
    </div>
  );
});
